<template>
  <div />
</template>

<script>
import WebAudioScheduler from 'web-audio-scheduler';
import * as stereoPannerNodePolyFill from 'stereo-panner-node';

import SoundDevice from '../sounds/SoundDevice';
import Noise from '../sounds/Noise';
import Impulse from '../sounds/Impulse';
import Kick from '../sounds/Kick';
import Arpeggiator from '../sounds/Arpeggiator';
import TwinArpeggiator from '../sounds/TwinArpeggiator';
import Pitch from '../sounds/Pitch';
import Dust from '../sounds/Dust';
import Bass from '../sounds/Bass';
import actions from '../../store/actions';

import defaults from './defaults';

export default {
  name: 'Scheduler',
  data() {
    return {
      context: undefined,
      master: undefined,
      scheduler: undefined,
      sounds: {
        kick: undefined,
        noise: undefined,
        impulse: undefined,
        arpeggiator: undefined,
      },
      metroTimes: defaults.getMetroTimes(),
      dust: undefined,
      bass: undefined,
    };
  },
  computed: {
    currentDataIndex: {
      get() {
        return this.$store.state.currentDataIndex;
      },
      set(idx) {
        this.$store.dispatch(actions.CHANGE_CURRENT_DATA_INDEX, idx);
      },
    },
    selectedCountry() {
      return this.$store.state.selectedCountry;
    },
    dataRange() {
      return this.$store.state.dataRange;
    },
    volume: {
      get() {
        return this.$store.state.volume;
      },
    },
    data() {
      return this.$store.state.country;
    },
    playing() {
      return this.$store.state.playing;
    },
    dataRefreshInterval() {
      return this.$store.state.dataRefreshInterval;
    },
  },
  watch: {
    playing(isPlaying) {
      if (isPlaying) {
        this.startPlaying();
      } else {
        this.stopPlaying();
      }
    },
    dataRange(newRange) {
      // Constrain currentDataIndex to new range
      const newIndex = this.constrain(this.currentDataIndex, ...newRange);
      this.currentDataIndex = newIndex;
    },
    volume(newVolume) {
      this.master.gain.setValueAtTime(newVolume, this.context.currentTime);
    },
    selectedCountry(position) {
      // Linking the Fundamental Frequency (200-400Hz) to the selected longitude (±180)
      this.harmony = defaults.getHarmony(position.lon);
      // console.log('Updating frequency: %s (%s)', this.harmony.fundFreq, position.countryName);
      this.refreshBassFundFreq(this.harmony.fundFreq);
      this.refreshArpeggiatorHarmony(this.harmony);
    },
  },
  mounted() {
    this.scheduler = new WebAudioScheduler({ context: this.context });
  },
  methods: {
    /**
     * Start playing of sound by creating a new context and starting
     * four functions in the scheduler.
     * Setup sounds.
     */
    startPlaying() {
      // Create audio context and external components (webkitAudioContext for safari)
      window.AudioContext = window.AudioContext || window.webkitAudioContext;
      // StereoPannerNode is not implemented in webkitAudioContext
      stereoPannerNodePolyFill.polyfill();

      this.context = new AudioContext();
      this.master = this.context.createGain();
      this.master.gain.cancelScheduledValues(this.context.currentTime);
      this.master.gain.setValueAtTime(this.volume, this.context.currentTime);
      this.master.connect(this.context.destination);

      this.harmony = defaults.getHarmony(0);

      // Setup sounds
      this.sounds.kick = new Kick(this.context, this.master);
      this.sounds.noise = new Noise(this.context, this.master);
      this.sounds.impulse = new Impulse(this.context, this.master);
      this.sounds.arpeggiator = new Arpeggiator(
        this.context,
        this.harmony,
        this.master,
        this.getMetroArpeggiatorTime,
      );
      this.sounds.twinArpeggiator = new TwinArpeggiator(
        this.context,
        this.harmony,
        this.master,
        this.sounds.arpeggiator,
      );
      this.dust = new Dust(this.context, this.master);
      this.bass = new Bass(this.context, this.master, this.harmony.fundFreq);
      Object.values(this.sounds).forEach((s) => s.setPlaying && s.setPlaying(true));

      // Start scheduler
      this.scheduler.start(this.updateData);
      this.scheduler.start(this.metroKick);
      this.scheduler.start(this.metroNoise);
      this.scheduler.start(this.metroImpulse);
      this.scheduler.start(this.metroArpeggiator);
    },

    /**
     * Stop playing of sound by stopping the scheduler and
     * closing the audio context.
     */
    stopPlaying() {
      Object.values(this.sounds).forEach((s) => s.setPlaying && s.setPlaying(false));
      this.scheduler.stop(true);
      this.context.close();
      this.metroTimes = defaults.getMetroTimes();
    },

    /**
     * Get current data according to current data index.
     * If no data is available, return null.
     */
    getCurrentDay() {
      if (this.data) {
        const idx = this.currentDataIndex;
        return this.data[idx];
      }
      return null;
    },

    /**
     * Step to next index, or loop around.
     */
    step() {
      this.currentDataIndex += 1;
      const [min, max] = this.dataRange;
      if (this.currentDataIndex > max) {
        this.currentDataIndex = min;
      }
    },

    // Functions to schedule with WebAudioScheduler:
    // All functions perform an action, then schedule themselves to
    // be reexecuted at a later time.

    /**
     * Call updateMetros, schedule itself for later.
     */
    updateData(e) {
      const t0 = e.playbackTime;
      this.updateSymphony();
      this.scheduler.insert(t0 + this.dataRefreshInterval, this.updateData);
      this.step();
    },

    /**
     * Gets next data point from data.
     * If data is available, updates metros, harmony, fade-in, and stuff.
     */
    updateSymphony() {
      const currentDay = this.getCurrentDay();

      if (!currentDay) return;

      console.log(currentDay.day);

      this.updateMetros(currentDay.aggregate);
      this.updateAllFadeInGains(currentDay.aggregate);
      this.updateHarmonyPitches(currentDay.aggregate);
      this.updateDustGain(currentDay.aggregate);
    },

    updateDustGain(data) {
      if (!this.dust) return;

      this.dust.updateGainFrom(data);
    },

    updateMetros(data) {
      this.metroTimes.kick = SoundDevice.scaleLinear(0, 1, 4, 0.3, data.confirmed);
      this.metroTimes.noise = SoundDevice.scaleLinear(0, 1, 3, 0.2, data.deaths);
      this.metroTimes.impulse = SoundDevice.scaleLinear(0, 1, 2, 0.7, data.recovered);

      this.metroTimes.arpeggiator = this.metroTimes.kick / 4;
    },

    updateAllFadeInGains(data) {
      Object.values(this.sounds).forEach((sound) => sound.updateFadeInGainFrom(data));
    },

    updateHarmonyPitches(data) {
      if (!this.sounds.arpeggiator) return;

      this.sounds.arpeggiator.incMetroIndex();
      const metroIdx = this.sounds.arpeggiator.harmony.metroIndex;

      const currentPitch = this.sounds.arpeggiator.harmony.pitches[metroIdx];

      if (currentPitch === undefined) {
        const newPitch = new Pitch(
          this.context,
          this.getDataRefreshInterval,
          this.sounds.arpeggiator.harmony,
          this.master,
        );
        newPitch.updateFrequenciesFrom(data.confirmed);
        this.sounds.arpeggiator.harmony.pitches.push(newPitch);
      } else {
        currentPitch.updateFrequenciesFrom(data.confirmed);
      }
    },

    refreshBassFundFreq(newFundFreq) {
      if (!this.bass) return;
      this.bass.setFundFreq(newFundFreq);
    },

    refreshArpeggiatorHarmony(newHarmony) {
      if (!this.sounds.arpeggiator) return;
      this.sounds.arpeggiator.setHarmony(newHarmony);
    },

    /**
     * Play arpeggiator sounds
     */
    metroArpeggiator(e) {
      const t0 = e.playbackTime;
      this.sounds.arpeggiator.update();
      this.sounds.arpeggiator.play();
      this.sounds.twinArpeggiator.update();
      this.sounds.twinArpeggiator.play();
      this.scheduler.insert(t0 + this.metroTimes.arpeggiator, this.metroArpeggiator);
    },

    /**
     * Play kick sound, schedule itself for later.
     */
    metroKick(e) {
      const t0 = e.playbackTime;
      this.sounds.kick.play();
      this.scheduler.insert(t0 + this.metroTimes.kick, this.metroKick);
    },

    /**
     * Play noise sound, schedule itself for later.
     */
    metroNoise(e) {
      const t0 = e.playbackTime;
      this.sounds.noise.play();
      this.scheduler.insert(t0 + this.metroTimes.noise, this.metroNoise);
    },

    /**
     * Play impulse sound, schedule itself for later.
     */
    metroImpulse(e) {
      const t0 = e.playbackTime;
      this.sounds.impulse.play();
      this.scheduler.insert(t0 + this.metroTimes.impulse, this.metroImpulse);
    },

    /**
     * Constrain a value inside a range.
     */
    constrain(v, min, max) {
      if (v < min) return min;
      if (v > max) return max;
      return v;
    },

    /**
     * Get current data refresh time.
     * This is just a function that is used as callback for classes that need this parameter.
     */
    getDataRefreshInterval() {
      return this.dataRefreshInterval;
    },

    /**
     * Get current metro kick time.
     * This is just a function that is used as callback for classes that need this parameter.
     */
    getMetroKickTime() {
      return this.metroTimes.kick;
    },

    /**
     * Get current metro arpeggiator time.
     * This is just a function that is used as callback for classes that need this parameter.
     */
    getMetroArpeggiatorTime() {
      return this.metroTimes.arpeggiator;
    },
  },
};
</script>

<style scoped></style>
